<template>
    <page :tabs="tabs" :busy="busy">
        <grid
            class="summary"
            ref="grid_summary"
            :defaultColDef="defaultColDef"
            :autoGroupColumnDef="autoGroupColumnDef"
            :columnDefs="columnDefsSummary"
            :treeData="true"
            :getDataPath="getDataPath"
            :rowData="gridSummaryRowData"
            :getRowStyle="getRowStyleSummary"
            :isSearch="false"
            :isAutoWidth="true"
            :isAutoHeight="true"
            gridTheme="ag-theme-alpine-report"
        >
            <date-range-picker
                :range-options="predefinedRanges"
                :value="dateRange"
                :date-format="userUnicodeDateFormat"
                @input="handleDaterangeChange"
            ></date-range-picker>
            <responsive-dropdown
                class="filter-button d-none d-lg-flex"
                :icon="['fas', 'filter']"
                :items="filterOptions"
                variant="primary"
                >{{ filterLabel }}
            </responsive-dropdown>
            <b-button-group class="d-none d-xl-flex">
                <search-multiselect
                    v-model="referrerSelected"
                    :options="referrerSelectOptions"
                    class="mr-1"
                    variant="primary"
                    itemLabel="referrer"
                    placeholder="Referrers"
                    controlValue="Referrers"
                ></search-multiselect>
            </b-button-group>
            <b-button-group :style="{ width: '140px' }" class="d-none d-lg-block mr-1">
                <form-square-switch
                    v-model="withCancelled"
                    @change="onWithCancelledChange"
                    :labels="{
                        checked: 'Show Cancelled',
                        unchecked: 'Show Cancelled',
                    }"
                    :is-toolbar="true"
                ></form-square-switch>
            </b-button-group>
            <export-to-excel-button :href="excelHref"></export-to-excel-button>
            <export-to-pdf-button :href="exportToPdfHref" :pdfCacheKey="detailsPdfCacheKey" />
            <responsive-dropdown
                class="filter-button d-none d-lg-flex"
                :icon="['fas', 'file']"
                :items="orientationFileOption"
                variant="primary"
                >{{ orientationFileLabel }}
            </responsive-dropdown>

            <b-button-group class="d-lg-none mr-1">
                <more-filters-dropdown>
                    <template v-slot:default="{ handleDropdownShow, handleDropdownHide }">
                        <responsive-dropdown
                            :items="filterOptions"
                            :responsiveness="false"
                            variant="primary"
                            @show="handleDropdownShow"
                            @hide="handleDropdownHide"
                            >{{ filterLabel }}
                        </responsive-dropdown>
                        <responsive-dropdown
                            :icon="['fas', 'file']"
                            :items="orientationFileOption"
                            :responsiveness="false"
                            variant="primary"
                            @show="handleDropdownShow"
                            @hide="handleDropdownHide"
                            >{{ orientationFileLabel }}
                        </responsive-dropdown>
                        <b-button-group>
                            <search-multiselect
                                v-model="referrerSelected"
                                :options="referrerSelectOptions"
                                class="mr-1"
                                variant="primary"
                                placeholder="Referrers"
                                controlValue="Referrers"
                                itemLabel="referrer"
                                @show="handleDropdownShow"
                                @hide="handleDropdownHide"
                            ></search-multiselect>
                        </b-button-group>
                        <form-square-switch
                            v-model="withCancelled"
                            @change="onWithCancelledChange"
                            :labels="{
                                checked: 'Show Cancelled',
                                unchecked: 'Show Cancelled',
                            }"
                        ></form-square-switch>
                    </template>
                </more-filters-dropdown>
            </b-button-group>
            <report-settings-modal :modalId="reportSettingsModalId" :initialValues="reportSettings" />
        </grid>
        <grid
            class="h-100 detail"
            ref="grid_detail"
            :isSearch="false"
            :columnDefs="columnDefsDetail"
            :rowData="gridDetailRowData"
            :statusBar="statusBar"
            gridTheme="ag-theme-alpine-report"
            :pinnedBottomRowData="pinnedBottomRowData"
            :getRowStyle="getRowStyleDetail"
        ></grid>
    </page>
</template>

<script>
    import API from '../../../api.jsx';
    import Page from '../../generic/page/page.vue';
    import grid from '../../generic/grid/grid.vue';
    import ResponsiveDropdown from '../../generic/responsive-dropdown/responsive-dropdown.vue';
    import SearchMultiselect from '../../generic/search-multiselect/search-multiselect.vue';
    import totalStatusBar from './total-status-bar.vue';
    import {
        REFERRAL_REPORT_SUMMARY_ENDPOINT,
        REFERRAL_REPORT_DETAIL_ENDPOINT,
        REFERRAL_REPORT_REFERRER_CHOICE_ENDPOINT,
        REFERRAL_REPORT_REFERRERS_ENDPOINT,
        REFERRAL_REPORT_UPDATE_REFERRER_CHOICE_ENDPOINT,
        REFERRAL_REPORT_UPDATE_REFERRER_ENDPOINT,
    } from '../../../endpoints';
    import DateRangePicker, {
        ALL_DATES,
        LAST_MONTH,
        LAST_YEAR,
        nameToRange,
        THIS_MONTH,
        THIS_YEAR,
        TODAY,
        YESTERDAY,
    } from '../../generic/date-range-picker/date-range-picker.vue';
    import ExportToExcelButton from '../../generic/export-to-excel-button/export-to-excel-button.vue';
    import MultiLineHeader from '../../common/multi-line-header.vue';
    import format from 'date-fns/format';
    import FormSelect from '../../generic/form-select/form-select.vue';
    import { mapGetters } from 'vuex';
    import ResponsiveButton from '../../generic/responsive-button/responsive-button.vue';
    import FormSquareSwitch from '../../generic/form-square-switch/form-square-switch.vue';
    import MoreFiltersDropdown from '../../generic/more-filters-dropdown/more-filters-dropdown.vue';
    import { escapeHTML } from '../../../utils/strings';
    import { toAmountDisplay } from '../../../utils/numbers';
    import { removeScrollMixin } from '../../../mixins/remove-scroll-mixin';
    import ReferrerCellRenderer from './referrer-cell-renderer.vue';
    import ReportSettingsModal, {
        SELECT_INPUT,
        DATE_PERIODS_INPUT,
        SWITCH_INPUT,
        MULTI_SELECT_INPUT,
    } from '../report-settings/report-settings-modal.vue';
import { defineAsyncComponent } from 'vue';

    const exportToPdfButton = defineAsyncComponent(() =>
        import(
            /* webpackChunkName: "lx-export-to-pdf-button" */
            '../../generic/export-to-pdf-button/export-to-pdf-button.vue'
        ))

    export default {
        components: {
            Page,
            grid,
            DateRangePicker,
            FormSelect,
            ResponsiveButton,
            FormSquareSwitch,
            MoreFiltersDropdown,
            ExportToExcelButton,
            ResponsiveDropdown,
            exportToPdfButton,
            ReportSettingsModal,
            SearchMultiselect,
            totalStatusBar: totalStatusBar,
            multiLineHeader: MultiLineHeader,
            referrerCellRenderer: ReferrerCellRenderer,
        },
        mixins: [removeScrollMixin],
        data() {
            return {
                busy: null,
                tabs: [
                    {
                        title: 'Referral Report',
                        route: '/app/reports/referral/report/',
                        active: true,
                    },
                ],
                withCancelled: true,
                filterBy: 'ENTERED_DATE',
                filterLabel: 'Filter By Entered Date',
                landscapeOrientationFileLabel: 'Landscape',
                portraitOrientationFileLabel: 'Portrait',
                orientationFileLabel: 'Landscape',
                pageOrientation: 'landscape',
                referrers: [],
                referrerSelected: [],
                referrerOptions: [],
                referrerOptionsData: [],
                // datepicker
                dateRange: nameToRange(THIS_MONTH),
                dateRequestFormat: 'yyyy-MM-dd',
                predefinedRanges: [TODAY, YESTERDAY, THIS_MONTH, LAST_MONTH, THIS_YEAR, LAST_YEAR, ALL_DATES],
                // grid
                gridSummaryRowData: [],
                gridDetailRowData: [],
                defaultColDef: {
                    minWidth: 80,
                },
                autoGroupColumnDef: {
                    minWidth: 150,
                    maxWidth: 300,
                    resizable: true,
                    headerName: 'Summary',
                },
                gridContainerEl: null,
                gridApiSummary: null,
                columnApiSummary: null,
                gridApiDetail: null,
                columnApiDetail: null,
                expanded: false,
                getDataPath: null,
                getRowStyle: null,
                totalDetails: null,
                averageDetails: null,
                summaryPdfCacheKey: null,
                detailsPdfCacheKey: null,
                reportSettingsModalId: 'report-settings-modal-id',
            };
        },
        beforeMount() {
            this.getDataPath = (data) => data.id_hierarchy;
            this.getRowStyleSummary = (params) => {
                if (!params.data) {
                    return {
                        fontWeight: 'bold',
                    };
                } else if (params.data.referrer === 'Total') {
                    params.node.pdfRowStyle = {
                        bold: true,
                    };
                    return {
                        fontWeight: 'bold',
                        fontSize: '16px',
                    };
                }
            };
            this.getRowStyleDetail = (params) => {
                if (params.node.rowPinned) {
                    return {
                        fontWeight: 'bold',
                    };
                }
            };
        },
        computed: {
            ...mapGetters('header', ['userUnicodeDateFormat', 'userCurrencySymbol']),
            totalDetailsDisplay() {
                return toAmountDisplay({
                    amount: this.totalDetails || 0,
                    currency: this.userCurrencySymbol,
                });
            },
            excelHref() {
                let url = '/app/reports/referral/summary/excel/';
                url += `?from_date=${this.startDateRequest}`;
                url += `&to_date=${this.endDateRequest}`;
                url += `&filter_by=${this.filterBy}`;
                url += `&with_cancelled=${this.withCancelled}`;
                if (this.referrerSelected.length) {
                    url += `&referrers=${this.referrerSelected.join(',')}`;
                }
                return url;
            },
            exportToPdfHref() {
                let url = '/app/reports/referral/summary/pdf/';
                url += `?from_date=${this.startDateRequest}`;
                url += `&to_date=${this.endDateRequest}`;
                url += `&filter_by=${this.filterBy}`;
                url += `&with_cancelled=${this.withCancelled}`;
                if (this.referrerSelected.length) {
                    url += `&referrers=${this.referrerSelected.join(',')}`;
                }
                return url;
            },
            orientationFileOption() {
                return [
                    {
                        label: this.landscapeOrientationFileLabel,
                        click: () => {
                            this.pageOrientation = 'landscape';
                            this.orientationFileLabel = this.landscapeOrientationFileLabel;
                        },
                    },
                    {
                        label: this.portraitOrientationFileLabel,
                        click: () => {
                            this.pageOrientation = 'portrait';
                            this.orientationFileLabel = this.portraitOrientationFileLabel;
                        },
                    },
                ];
            },
            columnDefsSummary() {
                return [
                    {
                        field: 'qty',
                        headerName: 'Quantity',
                        minWidth: 100,
                        cellClass: 'ag-right-aligned-cell',
                        headerClass: 'ag-right-aligned-header',
                        sort: 'desc',
                    },
                    {
                        field: 'total_in_percent',
                        headerName: '% Total',
                        valueGetter: ({ data }) =>
                            `${
                                data && data.total_in_percent !== undefined
                                    ? data.total_in_percent.toFixed(2) + '%'
                                    : ''
                            }`,
                        minWidth: 100,
                        sortable: false,
                    },
                    {
                        field: 'rental_charges',
                        headerName: 'Rental Charges',
                        type: 'amountColumn',
                        sortable: false,
                        minWidth: 120,
                    },
                    {
                        field: 'total_rc_in_percent',
                        headerName: '% Total',
                        valueGetter: ({ data }) =>
                            `${
                                data && data.total_rc_in_percent !== undefined
                                    ? data.total_rc_in_percent.toFixed(2) + '%'
                                    : ''
                            }`,
                        minWidth: 120,
                        sortable: false,
                        autoWidthExclude: true,
                    },
                ];
            },
            pinnedBottomRowData() {
                return [
                    {
                        referrer: '',
                        properties: [],
                        time_to_arrival: this.averageDetails,
                        r_charges: this.totalDetails,
                    },
                ];
            },
            columnDefsDetail() {
                return [
                    {
                        field: 'entered_date',
                        headerName: this.filterBy === 'ENTERED_DATE' ? 'Entered Date' : 'Arrival Date',
                        type: 'dateColumn',
                        sort: 'asc',
                        minWidth: 150,
                    },
                    {
                        field: 'booking_method',
                        headerName: 'Booking Method',
                        minWidth: 120,
                    },
                    {
                        field: 'referrer.name',
                        headerName: 'Referrer',
                        valueGetter: ({ data }) => data.referrer.name,
                        cellRenderer: 'referrerCellRenderer',
                        cellRendererParams: {
                            referrerOptionsGetter: () => this.referrerOptionsData,
                            updateReferrer: async (referrer, id) => {
                                this.busy = true;
                                try {
                                    await API.put(REFERRAL_REPORT_UPDATE_REFERRER_ENDPOINT(id), {
                                        referrer,
                                    });
                                    this.referrerOptionsData.push({
                                        value: referrer,
                                        text: referrer,
                                    });
                                    this.successToast({
                                        text: 'Referrer successfully updated',
                                    });
                                    await this.fetchDetailData();
                                    await this.fetchReferrerOptions();
                                } catch (e) {
                                    this.errorToast({
                                        text: 'Could not update referrer',
                                    });
                                } finally {
                                    this.busy = false;
                                }
                            },
                        },
                        minWidth: 250,
                        autoHeight: true,
                    },
                    {
                        field: 'time_to_arrival',
                        headerName: 'Lead Time (days)',
                        headerComponent: 'multiLineHeader',
                        headerComponentParams: {
                            items: ['Lead', 'Time (days)'],
                            itemClass: 'text-left lead-time',
                        },
                        maxWidth: 100,
                        valueGetter: ({data}) => data.time_to_arrival,
                    },
                    {
                        field: 'inquiry_source',
                        headerName: 'Inquiry Source	',
                        valueGetter: ({ data, node }) => {
                            if (node.rowPinned) {
                                return '';
                            }
                            return data.inquiry_source || 'None';
                        },
                        minWidth: 120,
                    },
                    {
                        field: 'booking_survey',
                        headerName: 'Booking Survey',
                        valueGetter: ({ data, node }) => {
                            if (node.rowPinned) {
                                return '';
                            }
                            return data.booking_survey || 'None';
                        },
                        minWidth: 120,
                    },
                    {
                        field: 'id',
                        headerName: 'Invoice Number',
                        cellRenderer: ({ data }) =>
                            data.id ? `<a href="/app/reservation/edit/invoice/${data.id}">${data.id}</a>` : '',
                        minWidth: 120,
                    },
                    {
                        field: 'status',
                        headerName: 'Status',
                        minWidth: 150,
                    },
                    {
                        field: 'properties',
                        headerName: 'Property',
                        valueGetter: ({ data }) =>
                            data && data.properties.map((item) => escapeHTML(item.name)).join(','),
                        cellRenderer: ({ data }) =>
                            data && data.properties.map((item) => escapeHTML(item.name)).join('<br />'),
                        autoHeight: true,
                        minWidth: 200,
                    },
                    {
                        field: 'r_charges',
                        headerName: 'Rental Charges',
                        type: 'amountColumn',
                        minWidth: 150,
                    },
                ];
            },
            statusBar() {
                return {
                    statusPanels: [
                        {
                            statusPanel: 'totalStatusBar',
                            align: 'right',
                            statusPanelParams: { context: this.totalDetails },
                        },
                    ],
                };
            },
            endDateRequest() {
                return format(this.dateRange.to, this.dateRequestFormat);
            },
            startDateRequest() {
                return format(this.dateRange.from, this.dateRequestFormat);
            },
            filterOptions() {
                return [
                    {
                        label: 'Filter By Arrival Date',
                        click: () => {
                            this.filterBy = 'ARRIVAL_DATE';
                            this.filterLabel = 'Filter By Arrival Date';
                            this.fetchAllData();
                        },
                    },
                    {
                        label: 'Filter By Entered Date',
                        click: () => {
                            this.filterBy = 'ENTERED_DATE';
                            this.filterLabel = 'Filter By Entered Date';
                            this.fetchAllData();
                        },
                    },
                ];
            },
            pageOrientationOptions() {
                return [
                    { text: 'Landscape', value: 'landscape' },
                    { text: 'Portrait', value: 'portrait' },
                ];
            },
            referrerSelectOptions() {
                return this.referrers.map((referrer) => ({
                    value: referrer,
                    text: referrer,
                }));
            },
            reportSettings() {
                return {
                    name: 'Referral Report',
                    report_sysname: 'REFERRAL',
                    settings: [
                        {
                            type: DATE_PERIODS_INPUT,
                            value: 'this_month',
                            label: 'Date Range',
                            name: 'date_range',
                            predefinedRanges: [TODAY, YESTERDAY, THIS_MONTH, LAST_MONTH, THIS_YEAR, LAST_YEAR],
                        },
                        {
                            type: SELECT_INPUT,
                            value: 'ARRIVAL_DATE',
                            name: 'filter_by',
                            options: [
                                {
                                    text: 'Filter By Arrival Date',
                                    value: 'ARRIVAL_DATE',
                                },
                                {
                                    text: 'Filter By Entered Date',
                                    value: 'ENTERED_DATE',
                                },
                            ],
                            label: 'Filter By',
                        },
                        {
                            type: SWITCH_INPUT,
                            value: false,
                            name: 'with_cancelled',
                            label: 'Show Cancelled',
                        },
                        {
                            type: MULTI_SELECT_INPUT,
                            value: [],
                            name: 'referrers',
                            label: 'Referrers',
                            placeholder: 'All Referrers',
                            options: this.referrerSelectOptions,
                        },
                    ],
                };
            },
        },
        methods: {
            onWithCancelledChange(event) {
                this.withCancelled = event.value;
                this.fetchAllData();
            },
            prepareRowData(arr, rowName, totalRentalCharges, totalQty) {
                if (arr.length) {
                    return [
                        {
                            referrer: 'Total',
                            qty: totalQty,
                            rental_charges: totalRentalCharges,
                            id: Math.random() + rowName.split(' ').join('_') + 'total',
                            id_hierarchy: [rowName],
                        },
                        ...arr.map((item) => {
                            return {
                                ...item,
                                referrer: item.referrer.name || item.referrer,
                                id_hierarchy: [rowName, item.referrer.name || item.referrer],
                                id: item.id + rowName.split(' ').join('_'),
                            };
                        }),
                    ];
                } else {
                    return [
                        {
                            referrer: 'Total',
                            id_hierarchy: [rowName],
                            id: Math.random() + rowName.split(' ').join('_') + 'total',
                            qty: 0,
                            rental_charges: 0,
                        },
                    ];
                }
            },
            async fetchReferrerOptions() {
                try {
                    const { data } = await API.get(REFERRAL_REPORT_REFERRER_CHOICE_ENDPOINT());
                    this.referrerOptions = data;
                    this.referrerOptionsData = this.referrerOptions.map((item) => ({
                        value: item.referer,
                        text: item.referer,
                    }));
                } catch (e) {
                    this.errorToast({ text: 'Could not fetch referrer data' });
                }
            },
            async fetchReferrers() {
                try {
                    const { data } = await API.get(REFERRAL_REPORT_REFERRERS_ENDPOINT(), {
                        params: {
                            from_date: this.startDateRequest,
                            to_date: this.endDateRequest,
                            filter_by: this.filterBy,
                            with_cancelled: this.withCancelled,
                        },
                    });
                    this.referrers = data;
                } catch (e) {
                    this.errorToast({ text: 'Could not fetch referrers' });
                }
            },
            async fetchDetailData() {
                try {
                    const { data } = await API.get(REFERRAL_REPORT_DETAIL_ENDPOINT(), {
                        params: {
                            from_date: this.startDateRequest,
                            to_date: this.endDateRequest,
                            filter_by: this.filterBy,
                            with_cancelled: this.withCancelled,
                            pdf_cache_key: this.summaryPdfCacheKey,
                            referrers: this.referrerSelected.join(','),
                        },
                    });
                    this.detailsPdfCacheKey = data.pdf_cache_key;
                    this.totalDetails = data.total;
                    this.averageDetails = data.average;
                    this.gridDetailRowData = data.details;
                } catch (e) {
                    this.errorToast({ text: 'Could not fetch detail data' });
                }
            },
            async fetchSummaryData() {
                try {
                    const { data } = await API.get(REFERRAL_REPORT_SUMMARY_ENDPOINT(), {
                        params: {
                            from_date: this.startDateRequest,
                            to_date: this.endDateRequest,
                            filter_by: this.filterBy,
                            with_cancelled: this.withCancelled,
                            referrers: this.referrerSelected.join(','),
                        },
                    });
                    this.summaryPdfCacheKey = data.pdf_cache_key;
                    const InqurySourceArray = this.prepareRowData(
                        data.summary.inquiry_source.list,
                        'Inquiry Source',
                        data.summary.inquiry_source.total_rental_charges,
                        data.summary.inquiry_source.total_qty
                    );
                    const referrerArray = this.prepareRowData(
                        data.summary.referrer.list,
                        'Referrer',
                        data.summary.referrer.total_rental_charges,
                        data.summary.referrer.total_qty
                    );
                    const bookingMethodArray = this.prepareRowData(
                        data.summary.booking_method.list,
                        'Booking Method',
                        data.summary.booking_method.total_rental_charges,
                        data.summary.booking_method.total_qty
                    );
                    const bookingSurveyArray = this.prepareRowData(
                        data.summary.booking_survey.list,
                        'Booking Survey',
                        data.summary.booking_survey.total_rental_charges,
                        data.summary.booking_survey.total_qty
                    );
                    this.gridSummaryRowData = [
                        ...referrerArray,
                        ...bookingMethodArray,
                        ...bookingSurveyArray,
                        ...InqurySourceArray,
                    ];
                } catch (e) {
                    this.errorToast({ text: 'Could not fetch data' });
                }
                setTimeout(() => this.expandLevel0());
            },
            async expandLevel0() {
                this.gridApiSummary.collapseAll();
                this.gridApiSummary.forEachNode((node) => {
                    if (node.level === 1) {
                        node.setExpanded(true);
                    }
                });
            },
            async fetchAllData() {
                this.busy = true;
                try {
                    await this.fetchSummaryData();
                    await this.fetchDetailData();
                    await this.fetchReferrerOptions();
                    await this.fetchReferrers();
                } finally {
                    this.busy = false;
                }
            },
            handleDaterangeChange(value) {
                this.dateRange = { ...value };
                this.fetchAllData();
            },
        },
        watch: {
            async referrerSelected() {
                this.busy = true;
                try {
                    await this.fetchSummaryData();
                    await this.fetchDetailData();
                } finally {
                    this.busy = false;
                }
            },
        },
        mounted() {
            const gridSummary = this.$refs.grid_summary;
            this.gridApiSummary = gridSummary.gridOptions.api;
            this.columnApiSummary = gridSummary.gridOptions.columnApi;
            const gridDetail = this.$refs.grid_detail;
            this.gridApiDetail = gridDetail.gridOptions.api;
            this.columnApiDetail = gridDetail.gridOptions.columnApi;

            this.fetchAllData();
        },
    };
</script>
<style lang="scss" scoped>
    @import '~bootstrap/scss/_functions.scss';
    @import '~bootstrap/scss/_variables.scss';
    @import '~bootstrap/scss/mixins/_breakpoints.scss';

    :deep(.ag-status-bar) {
        padding-right: 0 !important;
    }

    :deep(.lead-time) {
        font-size: 12px !important;
    }

    @include media-breakpoint-down(md) {
        .filter-button {
            height: 38px;
        }
    }

    .summary {
        display: inline;
    }

    .detail {
        display: inline;
        padding-left: 1px;
    }
</style>
